import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CreateCustomerGroupDto,
  FindAllCustomerGroupDto,
  ResponseDto,
  ResponsePagingDto,
  UpdateCustomerGroupDto,
  CustomerGroupDto
} from 'types';
import { mergeArrays } from 'utils';
import axiosBaseQuery from 'utils/base-api';

export const customerGroupsApi = createApi({
  reducerPath: 'customerGroupsApi',
  tagTypes: ['customer_groups', 'customer_groups_detail'],
  baseQuery: axiosBaseQuery,
  endpoints: (builder) => ({
    getCustomerGroups: builder.query<ResponsePagingDto<CustomerGroupDto>, FindAllCustomerGroupDto>({
      query: (params) => ({
        url: '/customer_groups',
        method: 'get',
        params
      }),
      providesTags: (result) =>
        result && result.data.rows.length > 0
          ? result.data.rows.map(({ customerGroupId }) => ({
              type: 'customer_groups',
              id: customerGroupId
            }))
          : ['customer_groups']
    }),

    getCustomerGroupDetail: builder.query<ResponseDto<CustomerGroupDto>, number>({
      query: (customerGroupId) => ({
        url: `/customer_groups/${customerGroupId}`,
        method: 'get'
      }),
      providesTags: (result) => (result?.data ? [{ type: 'customer_groups', id: result.data.customerGroupId }] : [])
    }),

    createCustomerGroup: builder.mutation<ResponseDto<CustomerGroupDto>, CreateCustomerGroupDto>({
      query: (newCustomerGroup) => ({
        url: '/customer_groups',
        method: 'post',
        data: newCustomerGroup
      }),
      invalidatesTags: ['customer_groups']
    }),
    updateCustomerGroup: builder.mutation<ResponseDto<CustomerGroupDto>, UpdateCustomerGroupDto>({
      query: ({ customerGroupId, ...updateCustomerGroup }) => ({
        url: `/customer_groups/${customerGroupId}`,
        method: 'patch',
        data: updateCustomerGroup
      }),
      invalidatesTags: (_result, _error, param) => [
        { type: 'customer_groups', id: param.customerGroupId },
        { type: 'customer_groups_detail', id: param.customerGroupId }
      ]
    }),
    deleteCustomerGroup: builder.mutation<ResponseDto<null>, number>({
      query: (customerGroupId) => ({
        url: `/customer_groups/${customerGroupId}`,
        method: 'delete'
      }),
      invalidatesTags: (_result, _error, param) => [
        { type: 'customer_groups', id: param },
        { type: 'customer_groups_detail', id: param }
      ]
    }),
    getCustomerGroupOptions: builder.query<ResponsePagingDto<CustomerGroupDto>, FindAllCustomerGroupDto>({
      query: ({ endpoint, ...params }) => ({
        url: '/customer_groups',
        method: 'get',
        params
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        if (queryArgs.endpoint) {
          return endpointName + queryArgs.endpoint;
        }
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.pageIndex !== 1) {
          currentCache.data.rows = mergeArrays(currentCache.data.rows, newItems.data.rows, 'customerGroupId');
        } else currentCache.data.rows = newItems.data.rows;
        currentCache.data.count = newItems.data.count;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.keyword !== previousArg?.keyword || currentArg?.pageIndex !== previousArg?.pageIndex;
      },
      providesTags: ['customer_groups']
    })
  })
});

export const {
  useGetCustomerGroupOptionsQuery,
  useGetCustomerGroupsQuery,
  useGetCustomerGroupDetailQuery,
  useCreateCustomerGroupMutation,
  useUpdateCustomerGroupMutation,
  useDeleteCustomerGroupMutation
} = customerGroupsApi;
